import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { clearProjectRole } from '@/composables/useProjectRole'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: '/dashboard',
  },
  // Auth / public routes
  {
    path: '/sign-in',
    name: 'SignIn',
    component: () => import('@/pages/SignInPage.vue'),
    meta: { layout: 'auth' },
  },
  {
    path: '/sign-up',
    name: 'SignUp',
    component: () => import('@/pages/SignUpPage.vue'),
    meta: { layout: 'auth' },
  },
  // Organization invitation landing. Clerk redirects invitation links here
  // (redirect_url set by the API's InviteToOrg) with __clerk_ticket/__clerk_status
  // in the URL; the page embeds Clerk's SignIn/SignUp to finish the org join.
  {
    path: '/accept-invitation',
    name: 'AcceptInvitation',
    component: () => import('@/pages/AcceptInvitation.vue'),
    meta: { layout: 'auth' },
  },
  // Onboarding wizard
  {
    path: '/onboarding',
    name: 'Onboarding',
    component: () => import('@/pages/onboarding/OnboardingWizard.vue'),
    meta: { requiresAuth: true, layout: 'wizard' },
  },
  {
    path: '/onboarding/new-project',
    name: 'OnboardingNewProject',
    component: () => import('@/pages/onboarding/OnboardingWizard.vue'),
    meta: { requiresAuth: true, layout: 'wizard' },
    props: { isFromDashboard: true },
  },
  // Organization settings (not project-scoped)
  {
    path: '/organization',
    name: 'Organization',
    component: () => import('@/pages/Organization.vue'),
    meta: { requiresAuth: true, layout: 'dashboard' },
  },
  // Billing (not project-scoped)
  {
    path: '/billing',
    name: 'Billing',
    component: () => import('@/pages/Billing.vue'),
    meta: { requiresAuth: true, layout: 'dashboard' },
  },
  // OAuth consent screen — the API's /authorize redirects browsers here
  // with ?state_token=XXX when a CLI (or other public client) initiates
  // login. requiresAuth sends unsigned-in users through Clerk and back.
  {
    path: '/oauth/authorize',
    name: 'OAuthAuthorize',
    component: () => import('@/pages/OAuthAuthorize.vue'),
    meta: { requiresAuth: true, layout: 'auth' },
  },
  // Dashboard routes (require auth)
  {
    path: '/dashboard',
    name: 'DashboardOverview',
    component: () => import('@/pages/DashboardOverview.vue'),
    meta: { requiresAuth: true, layout: 'dashboard' },
  },
  {
    path: '/dashboard/:project',
    name: 'ProjectDashboard',
    component: () => import('@/pages/ProjectDashboard.vue'),
    meta: { requiresAuth: true, layout: 'dashboard' },
  },
  {
    path: '/dashboard/:project/flags',
    name: 'ProjectFlags',
    component: () => import('@/pages/ProjectDashboard.vue'),
    meta: { requiresAuth: true, layout: 'dashboard' },
  },
  {
    path: '/dashboard/:project/flags/new',
    name: 'CreateFlag',
    component: () => import('@/pages/CreateFlag.vue'),
    meta: { requiresAuth: true, layout: 'dashboard' },
  },
  {
    path: '/dashboard/:project/flags/:key',
    name: 'FlagDetail',
    component: () => import('@/pages/FlagDetail.vue'),
    meta: { requiresAuth: true, layout: 'dashboard' },
  },
  {
    path: '/dashboard/:project/environments',
    name: 'Environments',
    component: () => import('@/pages/Environments.vue'),
    meta: { requiresAuth: true, layout: 'dashboard' },
  },
  {
    path: '/dashboard/:project/environments/new',
    name: 'NewEnvironment',
    component: () => import('@/pages/NewEnvironment.vue'),
    meta: { requiresAuth: true, layout: 'dashboard' },
  },
  {
    path: '/dashboard/:project/environments/:envName',
    name: 'EnvironmentDetail',
    component: () => import('@/pages/EnvironmentDetail.vue'),
    meta: { requiresAuth: true, layout: 'dashboard' },
  },
  {
    path: '/dashboard/:project/segments',
    name: 'Segments',
    component: () => import('@/pages/Segments.vue'),
    meta: { requiresAuth: true, layout: 'dashboard' },
  },
  {
    path: '/dashboard/:project/analytics',
    name: 'Analytics',
    component: () => import('@/pages/Analytics.vue'),
    meta: { requiresAuth: true, layout: 'dashboard' },
  },
  {
    path: '/dashboard/:project/audit-log',
    name: 'AuditLog',
    component: () => import('@/pages/AuditLog.vue'),
    meta: { requiresAuth: true, layout: 'dashboard' },
  },
  {
    path: '/dashboard/:project/settings',
    name: 'Settings',
    component: () => import('@/pages/Settings.vue'),
    meta: { requiresAuth: true, layout: 'dashboard' },
  },
  {
    path: '/dashboard/:project/versions',
    name: 'Versions',
    component: () => import('@/pages/Versions.vue'),
    meta: { requiresAuth: true, layout: 'dashboard' },
  },
  {
    path: '/dashboard/:project/schedule',
    name: 'Schedule',
    component: () => import('@/pages/Schedule.vue'),
    meta: { requiresAuth: true, layout: 'dashboard' },
  },
  {
    path: '/dashboard/:project/promotions',
    name: 'Promotions',
    component: () => import('@/pages/Promotions.vue'),
    meta: { requiresAuth: true, layout: 'dashboard' },
  },
  {
    path: '/dashboard/:project/playground',
    name: 'Playground',
    component: () => import('@/pages/FlagPlayground.vue'),
    meta: { requiresAuth: true, layout: 'dashboard' },
  },
  {
    path: '/dashboard/:project/change-requests',
    name: 'ChangeRequests',
    component: () => import('@/pages/ChangeRequests.vue'),
    meta: { requiresAuth: true, layout: 'dashboard' },
  },
  {
    path: '/dashboard/:project/change-requests/:crId',
    name: 'ChangeRequestDetail',
    component: () => import('@/pages/ChangeRequestDetail.vue'),
    meta: { requiresAuth: true, layout: 'dashboard' },
  },
  // Admin routes
  {
    path: '/admin',
    name: 'AdminDashboard',
    component: () => import('@/pages/admin/AdminDashboard.vue'),
    meta: { requiresAuth: true, layout: 'admin' },
  },
  {
    path: '/admin/customers',
    name: 'AdminCustomers',
    component: () => import('@/pages/admin/AdminCustomers.vue'),
    meta: { requiresAuth: true, layout: 'admin' },
  },
  {
    path: '/admin/customers/:orgId',
    name: 'AdminOrgDetail',
    component: () => import('@/pages/admin/AdminOrgDetail.vue'),
    meta: { requiresAuth: true, layout: 'admin' },
  },
  {
    path: '/admin/subscriptions',
    name: 'AdminSubscriptions',
    component: () => import('@/pages/admin/AdminDashboard.vue'),
    meta: { requiresAuth: true, layout: 'admin' },
  },
  {
    path: '/admin/system-health',
    name: 'AdminSystemHealth',
    component: () => import('@/pages/admin/AdminSystemHealth.vue'),
    meta: { requiresAuth: true, layout: 'admin' },
  },
  // Catch-all 404
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/pages/NotFound.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

// Deliberately does no fetching. Guards run before any component mounts, so a
// request here goes out before Clerk has loaded — which is what made the role
// fail to resolve on a cold load. DashboardLayout owns resolution instead: it
// only renders once Clerk is ready, so the request always carries a token.
router.beforeEach((to) => {
  const slug = typeof to.params.project === 'string' ? to.params.project : ''
  if (!slug) clearProjectRole()
  return true
})

export default router
